
export const normalizePhoneNumber = (phone: string | number): string => {
    return String(phone).replace(/\D/g, '');
};

export const isValidPhoneNumber = (phone: string): boolean => {
    return /^\d{10,15}$/.test(phone);
};

export const truncateText = (str: string, length: number): string => {
    if (!str || typeof str !== 'string') return '';
    return str.length > length ? str.substring(0, length) + '...' : str;
};

export const generateInitials = (name: string): string => {
    return name ? name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '#';
};

/**
 * Handles various timestamp formats (Firestore Timestamp, ISO string, Date object)
 * and returns a standard date string (e.g., "Oct 24, 2024, 10:30 AM").
 */
export const formatDateTime = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    
    let date: Date;
    
    if (timestamp._seconds) {
        // Firestore Timestamp
        date = new Date(timestamp._seconds * 1000);
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        date = new Date(timestamp);
    } else if (timestamp instanceof Date) {
        date = timestamp;
    } else {
        return 'Invalid Date';
    }

    // Check for invalid date
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
    });
};

/**
 * Returns a relative time string for chat interfaces.
 * If today: "10:30 AM"
 * If older: "Oct 24"
 */
export const formatRelativeTime = (timestamp: any): string => {
    const time = getTimeMs(timestamp);
    if (!time) return '';
    
    const date = new Date(time);
    const now = new Date();
    const isToday = date.getDate() === now.getDate() && 
                    date.getMonth() === now.getMonth() && 
                    date.getFullYear() === now.getFullYear();
    
    if (isToday) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// Helper to extract milliseconds from any timestamp format
export const getTimeMs = (t: any): number => {
    if (!t) return 0;
    if (typeof t === 'number') return t;
    if (typeof t.toMillis === 'function') return t.toMillis();
    if (typeof t.toDate === 'function') return t.toDate().getTime();
    if (t._seconds !== undefined) return t._seconds * 1000 + (t._nanoseconds ? t._nanoseconds / 1000000 : 0);
    if (t.seconds !== undefined) return t.seconds * 1000 + (t.nanoseconds ? t.nanoseconds / 1000000 : 0);
    if (t instanceof Date) return t.getTime();
    if (typeof t === 'string') {
        const parsed = Date.parse(t);
        return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
};

export const getTimestampISO = (t: any): string | undefined => {
    if (!t) return undefined;
    const ms = getTimeMs(t);
    return ms > 0 ? new Date(ms).toISOString() : undefined;
};

/**
 * Dynamically loads an external script tag.
 * Used for performance optimization to defer heavy libraries until needed.
 */
export const loadScript = (src: string, id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (document.getElementById(id)) {
            // Script already loaded or loading
            const existingScript = document.getElementById(id) as HTMLScriptElement;
            if (existingScript.dataset.loaded === 'true') {
                resolve();
            } else {
                // If it's already in the DOM but not marked loaded, append listener
                existingScript.addEventListener('load', () => resolve());
                existingScript.addEventListener('error', (err) => reject(err));
            }
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.id = id;
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
            script.dataset.loaded = 'true';
            resolve();
        };
        script.onerror = (err) => {
            reject(err);
        };
        
        document.body.appendChild(script);
    });
};
